/**
 * Print-run matrix: sequenced steps with order filters + paper parts.
 * Part flags are opt-in (false unless listed). Use named factories for common runs.
 */

import {
  getPrintingOrderDeliveryDay,
  getPrintingOrderDispatchDay,
  getPrintingOrderShippingMethod,
} from './printingToolkitFilterOptions';
import { getWaveOrderKey } from './wavePickListModel';
import { orderPassesAnyPackersPlannerView } from './packersPlannerViewUtils';

export const PRINT_RUN_PARTS = Object.freeze({
  pickList: 'pickList',
  packingSlip: 'packingSlip',
  cheetah: 'cheetah',
  go4: 'go4',
  inscription: 'inscription',
  carrier: 'carrier',
  separator: 'separator',
});

export const PRINT_RUN_PART_LABELS = Object.freeze({
  [PRINT_RUN_PARTS.pickList]: 'Pick list',
  [PRINT_RUN_PARTS.packingSlip]: 'Standard A4 packing slip',
  [PRINT_RUN_PARTS.cheetah]: 'Cheetah LZ17',
  [PRINT_RUN_PARTS.go4]: 'GO4 (A4 + GoDEX outside)',
  [PRINT_RUN_PARTS.inscription]: 'A6 inscription',
  [PRINT_RUN_PARTS.carrier]: 'Shipping / GoDEX label',
  [PRINT_RUN_PARTS.separator]: 'Start-of segment sheet',
});

const PART_KEYS = Object.values(PRINT_RUN_PARTS);

const id = (prefix) =>
  `${prefix}-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`}`;

/**
 * Opt-in parts only — nothing is on unless listed in `enabled` or true in `overrides`.
 * @param {string[]|{[key:string]:boolean}} enabledOrOverrides
 */
export function createPrintRunParts(enabledOrOverrides = []) {
  const parts = Object.fromEntries(PART_KEYS.map((key) => [key, false]));
  if (Array.isArray(enabledOrOverrides)) {
    enabledOrOverrides.forEach((key) => {
      if (PART_KEYS.includes(key)) parts[key] = true;
    });
  } else if (enabledOrOverrides && typeof enabledOrOverrides === 'object') {
    PART_KEYS.forEach((key) => {
      if (enabledOrOverrides[key] === true) parts[key] = true;
    });
  }
  if (parts.packingSlip && parts.cheetah) {
    parts.packingSlip = false;
  }
  return parts;
}

/** @deprecated use createPrintRunParts — kept as alias for callers */
export function createDefaultPrintRunParts(overrides = {}) {
  return createPrintRunParts(overrides);
}

export function createPrintRunStep(input = {}) {
  const parts = createPrintRunParts(input.parts || {});
  return {
    id: String(input.id || id('step')),
    label: String(input.label || 'Print step').trim() || 'Print step',
    shippingMethods: Array.isArray(input.shippingMethods)
      ? input.shippingMethods.map((value) => String(value || '').trim()).filter(Boolean)
      : [],
    deliveryDays: Array.isArray(input.deliveryDays)
      ? input.deliveryDays.map((value) => String(value || '').trim()).filter(Boolean)
      : [],
    dispatchDays: Array.isArray(input.dispatchDays)
      ? input.dispatchDays.map((value) => String(value || '').trim()).filter(Boolean)
      : [],
    viewIds: Array.isArray(input.viewIds)
      ? input.viewIds.map((value) => String(value || '').trim()).filter(Boolean)
      : [],
    orderKeys: Array.isArray(input.orderKeys)
      ? input.orderKeys.map((value) => String(value || '').replace(/^#/, '').trim()).filter(Boolean)
      : [],
    parts,
  };
}

/**
 * Build a matrix from an ordered list of step specs (the clean run-method API).
 * @param {Array<object|string>} stepSpecs
 */
export function createPrintRunMatrixFromSteps(stepSpecs = []) {
  const list = Array.isArray(stepSpecs) ? stepSpecs.filter(Boolean) : [];
  if (!list.length) return createEmptyPrintRunMatrix();
  return {
    version: 1,
    steps: list.map((spec) =>
      typeof spec === 'string'
        ? createPrintRunStep({ label: spec, parts: createPrintRunParts([PRINT_RUN_PARTS.packingSlip]) })
        : createPrintRunStep(spec),
    ),
  };
}

export function createEmptyPrintRunMatrix() {
  return createPrintRunMatrixFromSteps([
    {
      label: 'All selected orders · Standard',
      parts: createPrintRunParts([PRINT_RUN_PARTS.packingSlip, PRINT_RUN_PARTS.separator]),
    },
  ]);
}

export function createAllPartsAllOrdersMatrix() {
  return createPrintRunMatrixFromSteps([
    {
      label: 'All parts · all orders',
      parts: createPrintRunParts([
        PRINT_RUN_PARTS.pickList,
        PRINT_RUN_PARTS.packingSlip,
        PRINT_RUN_PARTS.inscription,
        PRINT_RUN_PARTS.carrier,
        PRINT_RUN_PARTS.separator,
      ]),
    },
  ]);
}

export function createCarriersThenInscriptionsMatrix() {
  return createPrintRunMatrixFromSteps([
    {
      label: 'All shipping labels',
      parts: createPrintRunParts([PRINT_RUN_PARTS.carrier]),
    },
    {
      label: 'All inscriptions',
      parts: createPrintRunParts([PRINT_RUN_PARTS.inscription]),
    },
  ]);
}

export function createStandardThenCheetahMatrix() {
  return createPrintRunMatrixFromSteps([
    {
      label: 'Standard A4',
      parts: createPrintRunParts([PRINT_RUN_PARTS.packingSlip, PRINT_RUN_PARTS.separator]),
    },
    {
      label: 'Cheetah LZ17',
      parts: createPrintRunParts([PRINT_RUN_PARTS.cheetah, PRINT_RUN_PARTS.separator]),
    },
  ]);
}

/**
 * One step per shipping method, same parts on each (e.g. Royal Mail Standard then Bike Cheetah).
 * @param {Array<{ method: string, parts?: object|string[], label?: string }>} sequence
 */
export function createShippingMethodSequenceMatrix(sequence = []) {
  const steps = (sequence || [])
    .map((entry) => {
      const method = String(entry?.method || entry?.shippingMethod || '').trim();
      if (!method) return null;
      return createPrintRunStep({
        label: entry.label || method,
        shippingMethods: [method],
        parts: createPrintRunParts(entry.parts || [PRINT_RUN_PARTS.packingSlip]),
      });
    })
    .filter(Boolean);
  return steps.length
    ? { version: 1, steps }
    : createEmptyPrintRunMatrix();
}

/**
 * Named run methods for the Pack desk presets.
 */
export const PRINT_RUN_METHODS = Object.freeze({
  standardAll: createEmptyPrintRunMatrix,
  allParts: createAllPartsAllOrdersMatrix,
  carriersThenInscriptions: createCarriersThenInscriptionsMatrix,
  standardThenCheetah: createStandardThenCheetahMatrix,
  byShippingMethods: createShippingMethodSequenceMatrix,
  fromSteps: createPrintRunMatrixFromSteps,
});

/**
 * @param {object} order
 * @param {object} step
 * @param {{ plannerViews?: object[] }} [options]
 */
export function orderMatchesPrintRunStep(order, step, options = {}) {
  if (!order || !step) return false;
  const orderKey = getWaveOrderKey(order);
  if (step.orderKeys?.length) {
    if (!step.orderKeys.includes(orderKey)) return false;
  }
  if (step.shippingMethods?.length) {
    const method = getPrintingOrderShippingMethod(order) || 'Unspecified';
    if (!step.shippingMethods.includes(method)) return false;
  }
  if (step.deliveryDays?.length) {
    const day = getPrintingOrderDeliveryDay(order) || '';
    if (!step.deliveryDays.includes(day)) return false;
  }
  if (step.dispatchDays?.length) {
    const day = getPrintingOrderDispatchDay(order) || '';
    if (!step.dispatchDays.includes(day)) return false;
  }
  if (step.viewIds?.length) {
    const views = (options.plannerViews || []).filter((view) =>
      step.viewIds.includes(String(view.id)),
    );
    if (!views.length) return false;
    if (!orderPassesAnyPackersPlannerView(order, views)) return false;
  }
  return true;
}

/**
 * @param {object[]} orders
 * @param {object} step
 * @param {{ plannerViews?: object[] }} [options]
 */
export function filterOrdersForPrintRunStep(orders, step, options = {}) {
  return (orders || []).filter((order) => orderMatchesPrintRunStep(order, step, options));
}

export function describePrintRunStep(step, orderCount = 0) {
  const partLabels = PART_KEYS.filter((key) => step?.parts?.[key])
    .map((key) => PRINT_RUN_PART_LABELS[key])
    .filter(Boolean);
  const filters = [];
  if (step?.shippingMethods?.length) {
    filters.push(`method: ${step.shippingMethods.join(', ')}`);
  }
  if (step?.deliveryDays?.length) filters.push(`delivery: ${step.deliveryDays.join(', ')}`);
  if (step?.dispatchDays?.length) filters.push(`dispatch: ${step.dispatchDays.join(', ')}`);
  if (step?.viewIds?.length) filters.push(`${step.viewIds.length} view(s)`);
  if (step?.orderKeys?.length) filters.push(`${step.orderKeys.length} picked order(s)`);
  return {
    parts: partLabels.join(' · ') || 'No parts selected',
    filters: filters.join(' · ') || 'All orders in scope',
    orderCount,
  };
}

export function sanitizePrintRunMatrix(value) {
  if (!value || typeof value !== 'object') return createEmptyPrintRunMatrix();
  const steps = Array.isArray(value.steps) && value.steps.length
    ? value.steps.map((step) => createPrintRunStep(step))
    : createEmptyPrintRunMatrix().steps;
  return { version: 1, steps };
}
